import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiChevronRight, FiFilter, FiX } from 'react-icons/fi';
import API from '../../utils/api';
import ProductCard from '../../components/common/ProductCard';
import '../ProductsPage.css';
import './CatalogPages.css';

const SkeletonCard = () => (
  <div className="product-card skeleton-card">
    <div className="skeleton-img" />
    <div className="product-card-body">
      <div className="skeleton-line short" /><div className="skeleton-line" /><div className="skeleton-line medium" />
    </div>
  </div>
);

export default function CategoryPage() {
  const { categorySlug, seg1 } = useParams();
const resolvedCategorySlug = categorySlug || seg1;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [category,      setCategory]      = useState(null);
  const [products,      setProducts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [catLoading,    setCatLoading]    = useState(true);
  const [totalCount,    setTotalCount]    = useState(0);
  const [totalPages,    setTotalPages]    = useState(1);
  const [notFound,      setNotFound]      = useState(false);
  const [sidebarOpen,   setSidebarOpen]   = useState(false);

  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1');
  const subId = searchParams.get('subcategoryId') || '';

  // Load category
  useEffect(() => {
    setCatLoading(true);
    API.get(`/catalog/public/categories/${resolvedCategorySlug}`)
      .then(r => setCategory(r.data.category))
      .catch(() => setNotFound(true))
      .finally(() => setCatLoading(false));
  }, [resolvedCategorySlug]);

  // Load products
  const loadProducts = useCallback(() => {
    if (!category) return;
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12, sort, status: 'active', categoryId: category.id });
    if (subId) params.set('subcategoryId', subId);
    API.get(`/catalog/public/products?${params}`)
      .then(r => {
        setProducts(r.data.products || []);
        setTotalCount(r.data.count  || 0);
        setTotalPages(r.data.pages  || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, page, sort, subId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const updateParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  if (notFound) return (
    <div style={{ padding: '120px 0', textAlign: 'center' }}>
      <h2>Category not found</h2>
      <Link to="/products" className="btn btn-primary" style={{ marginTop: 16 }}>Back to Products</Link>
    </div>
  );

  const subcategories = category?.subcategories || [];
  const activeSub = subcategories.find(s => String(s.id) === String(subId));

  return (
    <>
      <Helmet>
        <title>{category?.seoTitle || `${category?.name || ''} | Angel Printers`}</title>
        <meta name="description" content={category?.seoDescription || category?.description || ''} />
        {category && <link rel="canonical" href={`${window.location.origin}/${category.slug}`} />}
      </Helmet>

      <div className="products-page">
        {/* Header */}
        <div className="products-header">
          <div className="container">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link><FiChevronRight size={13} />
              <span>{category?.name || '…'}</span>
              {activeSub && <><FiChevronRight size={13} /><span>{activeSub.name}</span></>}
            </nav>
            <div className="cat-header-layout">
              <div className="cat-header-text">
                <h1>{catLoading ? '…' : category?.name}</h1>
                {category?.description && <p className="cat-header-desc">{category.description}</p>}
                {/* <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', marginTop: 4 }}>
                  {totalCount} product{totalCount !== 1 ? 's' : ''}
                </p> */}
              </div>
              {category?.image && (
                <div className="cat-header-img">
                  <img src={category.image} alt={category.name} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="container products-layout">
          {/* Sidebar */}
          {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
          <aside className={`products-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
              <h3>Filters</h3>
              <button className="icon-btn" onClick={() => setSidebarOpen(false)}><FiX size={20} /></button>
            </div>

            {subcategories.length > 0 && (
              <div className="sidebar-section">
                <h4>Subcategory</h4>
                <div className="cat-list">
                  <button className={`cat-btn ${!subId ? 'active' : ''}`} onClick={() => updateParam('subcategoryId', '')}>All</button>
                  {subcategories.map(s => (
                    <button
                      key={s.id}
                      className={`cat-btn ${String(subId) === String(s.id) ? 'active' : ''}`}
                      onClick={() => { updateParam('subcategoryId', s.id); setSidebarOpen(false); }}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="sidebar-section">
              <h4>Sort By</h4>
              <select className="form-control" value={sort} onChange={e => updateParam('sort', e.target.value)}>
                <option value="newest">Newest</option>
                <option value="name">Name A–Z</option>
                <option value="price">Price: Low–High</option>
              </select>
            </div>

            {subId && (
              <button className="btn btn-outline btn-sm" style={{ width: '100%' }} onClick={() => updateParam('subcategoryId', '')}>
                Clear Filter
              </button>
            )}
          </aside>

          {/* Main */}
          <main className="products-main">
            <div className="products-toolbar">
              <button className="btn btn-outline btn-sm filter-toggle" onClick={() => setSidebarOpen(true)}>
                <FiFilter size={15} /> Filters
              </button>
              <span className="products-count">{totalCount} product{totalCount !== 1 ? 's' : ''}</span>
              <div style={{ flex: 1 }} />
              <select className="form-control toolbar-sort" value={sort} onChange={e => updateParam('sort', e.target.value)}>
                <option value="newest">Newest</option>
                <option value="name">Name A–Z</option>
                <option value="price">Price ↑</option>
              </select>
            </div>

            {/* Subcategory tiles */}
            {subcategories.length > 0 && !subId && (
              <div className="subcategory-tiles">
                {subcategories.map(s => (
                  <Link
                    key={s.id}
                    to={`/category/${resolvedCategorySlug}/${s.slug}`}
                    className="subcategory-tile"
                  >
                    {s.image && <img src={s.image} alt={s.name} loading="lazy" />}
                    <span>{s.name}</span>
                  </Link>
                ))}
              </div>
            )}

            {/* Active sub filter tag */}
            {activeSub && (
              <div className="active-filters">
                <span className="filter-tag">
                  {activeSub.name}
                  <button onClick={() => updateParam('subcategoryId', '')}><FiX size={12} /></button>
                </span>
              </div>
            )}

            {loading ? (
              <div className="products-grid">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : products.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">📦</div>
                <h3>No products yet</h3>
                <p>Check back soon or browse all products.</p>
                <Link to="/products" className="btn btn-primary">All Products</Link>
              </div>
            ) : (
              <div className="products-grid">
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}

            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" disabled={page <= 1} onClick={() => updateParam('page', page - 1)}>← Prev</button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
                  if (p < 1 || p > totalPages) return null;
                  return <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => updateParam('page', p)}>{p}</button>;
                })}
                <button className="page-btn" disabled={page >= totalPages} onClick={() => updateParam('page', page + 1)}>Next →</button>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}